import React, {FC, useCallback, useEffect, useState} from 'react';
import styles from './BorrowerWizard.module.scss';
import {onboardingStepToWizardStep, RefreshOnboardingOptions, WizardStep} from './types';
import {useWizardContext, WizardProvider} from './context';
import {SidePanel, StepDocuments, Stepper, StepScoring, StepStart, StepVerification} from './components';
import {cn} from '../../../../utils';
import {createBorrowingOnboardingManager} from '../../../../di';
import {useErrorCallback} from '../../../../hooks';
import {useAppDispatch} from '../../../../hooks';
import {setBorrowerOnboardingComplete} from '../../../../store/profile';
import type {PrescoreCheck, PrescoreValidationResult} from '../../../../api-client';
import {OnboardingStep} from '../../../../api-client';
import type {PrescoreDraft} from './types';

function lastPrescoreChecksToResult(checks: PrescoreCheck[]): PrescoreValidationResult | null {
  if (!checks?.length) return null;
  const [borrower, ...debitors] = checks;
  return {borrower, debitors};
}

function lastPrescoreChecksToDraft(checks: PrescoreCheck[]): PrescoreDraft {
  if (!checks?.length) {
    return {borrowerInn: '', debitorInns: ['']};
  }
  const [borrower, ...debitors] = checks;
  const borrowerInn = borrower?.inn ?? '';
  const debitorInns = debitors.length > 0 ? debitors.map((d) => d.inn) : [''];
  return {borrowerInn, debitorInns};
}

const BorrowerWizardContent = () => {
  const {activeStep, setActiveStep, setOnboardingStepStatus, setBackendCurrentStep, setOnboardingStepNote, setIsInitialDataLoaded, setLastPrescoreResult, setPrescoreDraft, setRefreshCallback} = useWizardContext();
  const [loading, setLoading] = useState(false);
  const handleError = useErrorCallback();
  const dispatch = useAppDispatch();

  const loadOnboardingState = useCallback(async (options?: RefreshOnboardingOptions) => {
    const silent = options?.silent === true;
    const keepCurrentStep = options?.keepCurrentStep === true;
    if (!silent) {
      setLoading(true);
      setIsInitialDataLoaded(false);
    }
    try {
      const manager = await createBorrowingOnboardingManager();
      let state;
      try {
        state = await manager.get();
      } catch (getErr) {
        state = await manager.create();
      }
      const wizardStep = onboardingStepToWizardStep(state.currentStep);
      setBackendCurrentStep(wizardStep);
      setOnboardingStepStatus(state.stepStatus);
      setOnboardingStepNote(state.stepNote);
      dispatch(setBorrowerOnboardingComplete(state.currentStep === OnboardingStep.PROJECT_CREATION));
      const checks = state.lastPrescoreChecks ?? [];
      const prescoreResult = lastPrescoreChecksToResult(checks);
      setLastPrescoreResult(prescoreResult);
      if (checks.length > 0) {
        setPrescoreDraft(lastPrescoreChecksToDraft(checks));
      }
      if (!keepCurrentStep) {
        setActiveStep(wizardStep);
      }
      setIsInitialDataLoaded(true);
    } catch (err: any) {
      await handleError(err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [setActiveStep, setOnboardingStepStatus, setBackendCurrentStep, setOnboardingStepNote, setLastPrescoreResult, setPrescoreDraft, setIsInitialDataLoaded, handleError]);

  useEffect(() => {
    setRefreshCallback(loadOnboardingState);
  }, [setRefreshCallback, loadOnboardingState]);

  useEffect(() => {
    (async () => {
      await loadOnboardingState();
    })();
  }, []);

  const showSidePanel = activeStep !== WizardStep.START;

  const renderStepContent = () => {
    switch (activeStep) {
    case WizardStep.START:
      return <StepStart />;
    case WizardStep.VERIFICATION:
      return <StepVerification />;
    case WizardStep.SCORING:
      return <StepScoring />;
    case WizardStep.DOCUMENTS:
      return <StepDocuments />;
    default:
      return <StepStart/>;
    }
  };

  return (
    <div className={styles.wizard}>
      <div className={styles.container}>
        <Stepper />
        
        <div className={cn(styles.layout, !showSidePanel && styles['layout--fullWidth'])}>
          <main className={styles.mainContent}>
            <div className={styles.stepContent}>
              {renderStepContent()}
            </div>
          </main>
          
          {showSidePanel && (
            <div className={styles.sidePanel}>
              <SidePanel />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface BorrowerWizardProps {
  initialStep?: WizardStep;
  className?: string;
}

const BorrowerWizard: FC<BorrowerWizardProps> = ({ 
  initialStep = WizardStep.START
}) => {
  return (
    <WizardProvider initialStep={initialStep}>
      <BorrowerWizardContent />
    </WizardProvider>
  );
};

export default BorrowerWizard;

